/**
 * Responsibility: the figures tab of the card dialog - the picture, the
 * numbers the decision turns on, the chart, and the links that let any of it
 * be checked by hand.
 *
 * The picture is centred against the taller figures column rather than pinned
 * to the top, where it looks dropped into the corner.
 *
 * The carousel shows this card and only this card: BetterPop's render, then
 * the matched catalogue's. It used to mix in photographs from live eBay slab
 * listings, and those are whatever a seller happened to photograph - a
 * different printing, a lot of four, the wrong card entirely. They are still
 * shown, but beside the listing they belong to on the listings tabs, where
 * they are attributed rather than presented as this card.
 *
 * The links sit last and centred, reading as a footer. They are what you reach
 * for after the numbers, not before.
 */
import Box from '@mui/material/Box';
import CircularProgress from '@mui/material/CircularProgress';
import Divider from '@mui/material/Divider';
import Stack from '@mui/material/Stack';
import type { CardDetail, DiscoveredCard } from '../../api/client';
import { ImageCarousel } from './ImageCarousel';
import { Figure, Outcome, OutLink, Row, Sold, EM_DASH } from './CardFigures';
import { CardChartArea } from '../cards/CardChartArea';
import { betterPopUrl, ebaySoldUrl, psaAprUrl } from './cardLinks';
import { money, multiple, percent } from './money';

/** Smaller than it was. The dialog has to hold figures, a chart and a row
 *  of links without scrolling, and the picture was taking the room. */
const IMAGE_WIDTH = 150;

type CardStatsProps = {
  card: DiscoveredCard;
  detail: CardDetail | undefined;
  loading: boolean;
  onZoom: (src: string | null | undefined, alt: string) => void;
};

export function CardStats({ card, detail, loading, onZoom }: CardStatsProps) {
  // Only renders of this card. BetterPop's first, since it is keyed to the
  // exact printing, then the matched catalogue's - which is the same card
  // by construction, because the matcher refuses anything it cannot place.
  const photos = [
    card.image_large_url ?? card.image_url,
    ...(detail?.market_images ?? []),
  ].filter((src): src is string => Boolean(src));

  return (
    <Stack spacing={1.5} sx={{ minWidth: 0, height: '100%' }}>
      <Stack
        direction={{ xs: 'column', sm: 'row' }}
        spacing={3}
        sx={{ minWidth: 0, flexShrink: 0 }}
      >
        <Stack spacing={0.75} alignItems="center" justifyContent="center" sx={{ flexShrink: 0 }}>
          <ImageCarousel
            images={photos}
            alt={card.card_name}
            width={IMAGE_WIDTH}
            onZoom={(src) => onZoom(src, card.card_name)}
          />
        </Stack>

        <Stack spacing={2} sx={{ flex: 1, minWidth: 0, justifyContent: 'center' }}>
          <Row>
            <Figure
              label="Raw cost"
              value={money(card.raw_cost)}
              hint={card.verified ? 'verified on TCGplayer' : 'reference only'}
            />
            <Figure
              label="PSA 10"
              value={money(card.reference_psa10)}
              hint={`${card.pop_psa10.toLocaleString()} exist · ${card.gem_rate ? percent(card.gem_rate, 1) : EM_DASH} gem`}
            />
            <Figure
              label="PSA 9"
              value={money(card.reference_psa9)}
              hint={`${card.pop_psa9.toLocaleString()} exist · ${card.nine_rate ? percent(card.nine_rate, 1) : EM_DASH} nine`}
            />
            <Figure
              label="Multiple"
              value={card.multiple ? multiple(card.multiple) : EM_DASH}
              hint={`${card.pop_total.toLocaleString()} graded`}
            />
          </Row>

          <Divider />

          <Row>
            <Outcome label="If one gems" value={card.best_case_profit} hint="rest sold as 9s" />
            <Outcome
              label="On average"
              value={card.expected_profit}
              hint={card.gem_rate ? `at ${percent(card.gem_rate, 1)}` : 'no gem rate'}
            />
            <Outcome
              label="If none gem"
              value={card.no_gem_profit}
              hint={card.downside_covered ? 'nines cover it' : 'money at risk'}
            />
            <Figure
              label="Pay up to"
              value={money(card.break_even_raw)}
              hint={`outlay ${money(card.total_cost)}`}
            />
          </Row>

          <Divider />

          <Row>
            <Sold label="Raw sold" sold={detail?.raw_sold} />
            <Sold label="PSA 9 sold" sold={detail?.psa9_sold} />
            <Sold label="PSA 10 sold" sold={detail?.psa10_sold} />
            {loading ? (
              <Stack justifyContent="center">
                <CircularProgress size={14} />
              </Stack>
            ) : null}
          </Row>
        </Stack>
      </Stack>

      <Divider sx={{ flexShrink: 0 }} />

      {/* Takes whatever room is left, so the dialog stays one height and
          the links below stay visible without scrolling. */}
      <Box sx={{ flex: 1, minHeight: 0 }}>
        <CardChartArea
          sales={detail?.market_sales ?? []}
          population={detail?.population_series ?? []}
          populationAdded={detail?.population_added ?? null}
          loading={loading}
          preferredGrade="PSA 10"
        />
      </Box>

      <Divider sx={{ flexShrink: 0 }} />

      <Stack
        direction="row"
        spacing={2.5}
        flexWrap="wrap"
        useFlexGap
        justifyContent="center"
        sx={{ flexShrink: 0 }}
      >
        {detail?.pricecharting_url ? (
          <OutLink
            href={detail.pricecharting_url}
            label="PriceCharting"
            hint="This exact card, with a price for every grade. No login."
          />
        ) : null}
        {detail?.tcgplayer_url ? (
          <OutLink
            href={detail.tcgplayer_url}
            label="TCGplayer"
            hint="The exact product the raw cost was verified against."
          />
        ) : null}
        <OutLink
          href={ebaySoldUrl(card, 'PSA 10')}
          label="eBay PSA 10"
          hint="Completed sales of PSA 10 copies."
        />
        <OutLink
          href={ebaySoldUrl(card)}
          label="eBay raw"
          hint="Completed sales of ungraded copies."
        />
        <OutLink
          href={betterPopUrl(card)}
          label="BetterPop"
          hint="Where the population figures came from."
        />
        {/* Three outcomes, and the label says which one you are getting.
            The card page when a spec id was resolved, the set page when
            only that came back, and the old keyword search when the
            certificate lookup did not get through at all. */}
        <OutLink
          href={detail?.apr_url ?? psaAprUrl(card)}
          label={
            detail?.apr_is_card
              ? 'PSA APR'
              : detail?.apr_url
                ? 'PSA APR (set)'
                : 'PSA APR (search)'
          }
          hint={
            detail?.apr_is_card
              ? `PSA's own realised prices for this exact card, spec ${detail.psa_spec_id}. Needs a PSA login.`
              : detail?.apr_url
                ? "PSA's realised prices for this set. The card's own spec id was not on the certificate page, so this lands on the set rather than the card."
                : "PSA's realised prices. A keyword search rather than the card page: the certificate lookup that resolves the real URL did not get through this time."
          }
        />
      </Stack>
    </Stack>
  );
}
